import 'uplot/dist/uPlot.min.css';

import {isEqual} from 'lodash';
import React, {
  type CSSProperties,
  type FC,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type uPlot from 'uplot';
import {type AlignedData} from 'uplot';
import UplotReact from 'uplot-react';

import {type IssueLine} from '../../../state/issues/types';
import {useDatadogRum} from '../../../util/datadog/datadogRum';
import {
  getAxisStyleForFontSize,
  type PlotFontSize,
} from '../../../util/plotHelpers/plotFontSize';
import {type PlotMargin} from '../../../util/plotHelpers/plotHelpers';
import {
  type Line,
  type LineType,
  type PlotType,
  type Timestep,
} from '../../../util/plotHelpers/types';
import {useRampFlagUPlotUser} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useColorMode} from '../../../util/useColorMode';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {usePanelConfigContext} from '../../PanelRunsLinePlot/PanelConfigContext';
import {useUpdateIconPositions} from '../LinePlotPlot';
import {type DomainMaybe} from '../types';
import {type IssueLinesConfig} from './drawIssueLines';
import {type NonFiniteMarkerState} from './drawNonFiniteMarkers';
import {getAlignedData} from './getAlignedData';
import {getSeriesEntries} from './getSeriesEntries';
import {getUPlotOptions} from './getUPlotOptions';
import {getuPlotLabelFontSizePx, getuPlotMarginOverride} from './margins';

const SUPPORTED_LINE_TYPES: Set<LineType> = new Set(['line', 'area']);

export function useShouldUseuPlot(
  lines: Line[],
  plotType: PlotType | undefined
) {
  const uplotEnabled = useRampFlagUPlotUser();
  const areAllLinesuPlotSupported = useMemo(() => {
    return lines.every(
      line => line.type != null && SUPPORTED_LINE_TYPES.has(line.type)
    );
  }, [lines]);
  const isChartTypeuPlotSupported = plotType === 'line';
  const panelConfig = usePanelConfigContext();
  return (
    uplotEnabled &&
    areAllLinesuPlotSupported &&
    isChartTypeuPlotSupported &&
    panelConfig.forceReactVis !== true
  );
}

const emptyIssueLines: IssueLine[] = [];

type LinePlotuPlotProps = {
  lines: Line[];
  width: number;
  height: number;
  xDomain: DomainMaybe;
  yDomain: DomainMaybe;
  xAxis: string;
  yAxisTitle?: string;
  fontSize: PlotFontSize;
  timestep: Timestep | undefined;
  /** Issue lines to render on the chart */
  issueLines?: IssueLine[];
  /** ID of the currently hovered issue (for highlighting) */
  hoveredIssue?: string | null;
  /** Whether the x-axis min is zoomed (if true, hide 'before' lines) */
  isZoomedXMin?: boolean;
  /** Whether the x-axis max is zoomed (if true, hide 'after' lines) */
  isZoomedXMax?: boolean;
  /** If true, this is the overlay layer that contains the highlighted lines */
  isOverlay?: boolean;
  dimFactor?: number;
  /** Use to override the default react-vis plot margin in the crosshair component */
  plotMarginOverride: PlotMargin | undefined;
  setPlotMarginOverride?: (plotMargin: PlotMargin) => void;
};

export const LinePlotuPlot: FC<LinePlotuPlotProps> = ({
  lines,
  width,
  height,
  xDomain,
  yDomain,
  xAxis,
  yAxisTitle,
  fontSize,
  timestep,
  issueLines = emptyIssueLines,
  hoveredIssue = null,
  isZoomedXMin = false,
  isZoomedXMax = false,
  isOverlay = false,
  dimFactor,
  plotMarginOverride,
  setPlotMarginOverride,
}) => {
  const panelID = useStablePanelId();

  const seriesEntriesResult = useMemo(() => getSeriesEntries(lines), [lines]);
  const {xAxisIsDatetime, xLogScale, yLogScale} = usePanelConfigContext();
  // update the issues line icon position to match the plot margin
  useUpdateIconPositions(xDomain, plotMarginOverride);

  const data: AlignedData = useMemo(
    () => getAlignedData(seriesEntriesResult.seriesEntries, xLogScale),
    [seriesEntriesResult, xLogScale]
  );
  const uplotRef = useRef<uPlot | null>(null);

  // State ref for non-finite markers - mutable state that draw/setCursor hooks can access
  const nonFiniteMarkerStateRef = useRef<NonFiniteMarkerState>({
    hoveredMarkerIndex: -1,
    dimFactor,
  });
  nonFiniteMarkerStateRef.current.dimFactor = dimFactor;

  const issueLinesConfig: IssueLinesConfig = useMemo(
    () => ({
      issueLines,
      hoveredIssue,
      isZoomedXMin,
      isZoomedXMax,
    }),
    [issueLines, hoveredIssue, isZoomedXMin, isZoomedXMax]
  );

  const {isDarkMode} = useColorMode();

  /**
   * Options are memoized and should avoid updating unless you want to re-render the entire plot.
   * If you need to access some state that changes in a uplot hook (like the nonFiniteMarkerState),
   * it's better to do it via a ref that can be accessed in the hook. This way the options itself
   * don't change and re-render the entire plot.
   */
  const options = useMemo(
    () =>
      getUPlotOptions(
        seriesEntriesResult.seriesEntries,
        seriesEntriesResult.lineNameMap,
        lines,
        nonFiniteMarkerStateRef,
        issueLinesConfig,
        width,
        height,
        xDomain,
        yDomain,
        xAxisIsDatetime,
        xLogScale,
        yLogScale,
        fontSize,
        timestep,
        isDarkMode,
        isOverlay
      ),
    [
      seriesEntriesResult,
      lines,
      issueLinesConfig,
      width,
      height,
      xDomain,
      yDomain,
      xAxisIsDatetime,
      xLogScale,
      yLogScale,
      fontSize,
      timestep,
      isDarkMode,
      isOverlay,
    ]
  );
  const lastBBoxRef = useRef<uPlot.BBox | null>(null);

  // has to be in useEffect to avoid bad setState call
  useEffect(() => {
    // Only use bbox values when uPlot is fully ready (status === 1)
    // status 0 means the chart is still initializing and bbox values are incorrect
    // This way we can avoid overtriggering updating the plot margin override.
    if (
      setPlotMarginOverride &&
      uplotRef.current != null &&
      uplotRef.current.status === 1 &&
      !isEqual(uplotRef.current.bbox, lastBBoxRef.current)
    ) {
      lastBBoxRef.current = uplotRef.current.bbox;

      // This aligns the crosshair overlay on top of the uPlot chart no matter what the
      // margins are automatically set by uPlot.
      const margin = getuPlotMarginOverride(
        uplotRef.current.width,
        uplotRef.current.height,
        lastBBoxRef.current
      );
      if (margin) {
        setPlotMarginOverride(margin);
      }
    }
  }, [
    setPlotMarginOverride,
    uplotRef.current?.bbox,
    uplotRef.current?.width,
    uplotRef.current?.height,
    lastBBoxRef.current?.width,
    lastBBoxRef.current?.height,
    uplotRef.current?.status,
  ]);
  const tickLabelStyle = getAxisStyleForFontSize(fontSize);
  const labelStyle = {
    fontSize: getuPlotLabelFontSizePx(fontSize),
    fontFamily: tickLabelStyle.fontFamily as CSSProperties['fontFamily'],
    color: tickLabelStyle.fill as string,
  };

  const smoothedFromBackend = lines.some(
    l => l.smoothedLineFromBackend != null
  );

  const hasSmoothedLines = smoothedFromBackend || lines.some(l => l.smoothed);

  const ddRum = useDatadogRum();
  useEffect(() => {
    if (hasSmoothedLines) {
      ddRum.addAction('line_plot_smoothed', {
        panelID,
        smoothedFromBackend,
        uplot: true,
      });
    }
  }, [ddRum, panelID, hasSmoothedLines, smoothedFromBackend]);

  return (
    <div
      className="w-full h-full relative"
      data-test="line-plot-uplot"
      data-smoothed-from-backend={smoothedFromBackend}>
      <UplotReact
        options={options}
        data={data}
        onCreate={u => {
          uplotRef.current = u;
        }}
        className="night-aware"
      />
      {/* x and y axis labels are rendered here to avoid them taking up space in the plot. */}
      {plotMarginOverride && (
        <div
          className="absolute leading-none"
          style={{
            ...labelStyle,
            bottom: (plotMarginOverride?.bottom ?? 0) + 6, // above the x axis
            right: 4,
          }}>
          {xAxis}
        </div>
      )}
      {yAxisTitle && plotMarginOverride && (
        <div
          className="absolute leading-none -rotate-90"
          style={{
            ...labelStyle,
            top: 14,
            left: (plotMarginOverride?.left ?? 0) + 2, // to the right of the y axis
          }}>
          {yAxisTitle}
        </div>
      )}
    </div>
  );
};
